import { CLink } from '@/CLink';
import { PrimaryBtn } from '@/buttons';
import { getNavLink } from '@/layout/NavLinks';
import { flexCenter, flexStart, npx, px } from '@/layout/styles/classes';
import { ReactComponent as AllIcon } from '@a/images/home/hero/all.svg';
import { ReactComponent as DefenseIcon } from '@a/images/home/hero/defense.svg';
import styled from '@emotion/styled';
import { StaticImage } from 'gatsby-plugin-image';

const Section = styled.section`
    ${px};
    padding-top: 88.8px;
    background-color: ${({ theme }) => theme.colors.blue1};

    @media (max-width: 339px) {
        padding-top: 82.8px;
    }

    > h1 {
        text-align: center;
        font-size: 2.25rem;
        letter-spacing: -0.54px;
        text-align: center;
        color: ${({ theme }) => theme.colors.blue5};
        margin: 0 0 -24px;
        z-index: 2;
        position: relative;
    }

    @media (min-width: 1024px) {
        padding-top: 104.8px;
        padding-bottom: 32px;

        > h1 {
            font-size: 4rem;
            letter-spacing: -1px;
            margin-bottom: -34px;
        }
    }

    @media (min-width: 1280px) {
        padding-top: 148.8px;
        padding-bottom: 64px;

        > h1 {
            font-size: 8rem;
            letter-spacing: -1.92px;
            margin-bottom: -54px;
        }
    }
`;

const Box = styled.div`
    > .gatsby-image-wrapper {
        z-index: 1;
        display: block;
        max-width: max-content;
        margin: 0 auto;

        ::before {
            content: '';
            position: absolute;
            top: 32%;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: -1;
            border-radius: 200px 4px;
            background-color: ${({ theme }) => theme.colors.blue2};
        }
    }

    @media (min-width: 1024px) {
        ${flexCenter};
        > .gatsby-image-wrapper {
            margin: 0;

            ::before {
                top: 0;
            }
        }
    }

    @media (min-width: 1280px) {
        > .gatsby-image-wrapper {
            overflow: visible;

            ::before {
                left: -60px;
                right: -60px;
            }
        }
    }
`;

const Explore = styled.div<{ mobile?: boolean }>`
    max-width: max-content;
    display: ${({ mobile }) => (mobile ? 'block' : 'none')};
    margin: -24px auto 0;
    z-index: 2;
    position: relative;

    > a {
        padding: 12px 24px;

        :first-of-type {
            display: flex;
            margin: 0 auto 16px;
            max-width: max-content;
        }

        :last-of-type {
            display: none;
        }
    }

    > p {
        :first-of-type {
            color: ${({ theme }) => theme.colors.gray3};
            letter-spacing: 0.08px;
            margin: 0;
        }

        :last-of-type {
            margin: 0;
            color: ${({ theme }) => theme.colors.blue7};
            font-weight: 500;
        }
    }

    @media (min-width: 1024px) {
        max-width: 294px;
        display: ${({ mobile }) => (mobile ? 'none' : 'block')};
        min-width: 240px;
        margin: 0;

        > a {
            padding: 16px 47px;

            :first-of-type {
                display: none;
            }

            :last-of-type {
                display: inline-flex;
            }
        }

        > p {
            :first-of-type {
                font-size: 1.325rem;
            }

            :last-of-type {
                font-size: 1.625rem;
                letter-spacing: 0.085px;
                margin: 16px 0;
            }
        }
    }

    @media (min-width: 1280px) {
        margin-left: 83px;

        > p {
            :first-of-type {
                font-size: 1.625rem;
            }

            :last-of-type {
                font-size: 2.125rem;
                margin: 24px 0;
            }
        }
    }
`;

const Features = styled.div`
    ${flexStart};
    justify-content: center;
    gap: 8px;
    padding-top: 48px;
    z-index: 2;
    position: relative;
    padding-bottom: 16px;

    ::before {
        ${npx};
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: linear-gradient(180deg, #dcf6fc 0, #dcf6fc 80px, white 80px, white);
        z-index: -1;
    }

    @media (min-width: 1024px) {
        flex-direction: column;
        gap: 40px;

        ::before {
            content: none;
        }
    }
`;

const Feature = styled.div`
    ${flexStart};
    align-items: center;
    flex-direction: column;
    max-width: 250px;
    flex: 1;

    > svg {
        width: 48px;
        height: 48px;
    }

    > div {
        margin-top: 24px;

        > p {
            text-align: center;

            :first-of-type {
                color: ${({ theme }) => theme.colors.blue7};
                font-size: 1.25rem;
                line-height: 140%;
                font-weight: 500;
                margin: 0 0 4px;
            }

            :last-of-type {
                color: ${({ theme }) => theme.colors.gray3};
                margin: 0;
                font-size: 0.875rem;
            }
        }
    }

    @media (min-width: 1024px) {
        flex-direction: row;
        max-width: 377px;

        > svg {
            flex-shrink: 0;
            width: 74px;
            height: 74px;
        }

        > div {
            margin-top: 0;
            margin-left: 24px;

            > p {
                text-align: left;

                :first-of-type {
                    font-weight: 600;
                    font-size: 1.325rem;
                }

                :last-of-type {
                }
            }
        }
    }

    @media (min-width: 1280px) {
        > div {
            > p:first-of-type {
                font-size: 1.625rem;
            }
        }
    }
`;

export const Hero = () => {
    return (
        <Section>
            <h1>Essential Minerals</h1>
            <Box>
                <Explore>
                    <PrimaryBtn as={CLink} to={getNavLink('shop all')}>
                        explore
                    </PrimaryBtn>
                    <p>Most Bioavailable Minerals</p>
                    <p>Get All & More</p>
                    <PrimaryBtn as={CLink} to={getNavLink('shop all')}>
                        explore
                    </PrimaryBtn>
                </Explore>
                <StaticImage
                    src="../../../assets/images/home/hero/hero-bottle.png"
                    alt="smart az minerals ez-8 bottle"
                    width={495}
                    height={646}
                />

                <Explore mobile>
                    <PrimaryBtn as={CLink} to={getNavLink('shop all')}>
                        explore
                    </PrimaryBtn>
                    <p>Most Bioavailable Minerals</p>
                    <p>Get All & Minerals</p>
                    <PrimaryBtn as={CLink} to={getNavLink('shop all')}>
                        explore
                    </PrimaryBtn>
                </Explore>
                <Features>
                    <Feature>
                        <DefenseIcon />
                        <div>
                            <p>Immune Defense</p>
                            <p>All 8 Ionic Minerals supplements in one bottle.</p>
                        </div>
                    </Feature>
                    <Feature>
                        <AllIcon />
                        <div>
                            <p>8 - All in One</p>
                            <p>
                                Daily dose of Boron, Calcium, Copper, Iodine, Indium, Magnesium,
                                Potassium, and Zinc.
                            </p>
                        </div>
                    </Feature>
                </Features>
            </Box>
        </Section>
    );
};
